import { Transaction, Portfolio } from "types/Transactions";
import { totalCosts } from "./totalCosts";

const sumForPrice = (portfolio: Transaction[], transactionMainType: string) => {
  const filteredTransactions = portfolio.filter(
    (transaction) => transaction.TransactionMainType === transactionMainType
  );
  if (filteredTransactions.length === 0) {
    return undefined;
  }

  return filteredTransactions
    .map((transaction) => transaction.CleanAmount)
    .reduce((prev, curr) => prev + curr, 0);
};

const sumForFee = (portfolio: Transaction[], transactionMainType: string) => {
  const filteredTransactions = portfolio.filter(
    (transaction) => transaction.TransactionMainType === transactionMainType
  );
  if (filteredTransactions.length === 0) {
    return undefined;
  }

  return filteredTransactions
    .map((transaction) => transaction.Fee)
    .reduce((prev, curr) => prev + curr, 0);
};

const sumForTax = (portfolio: Transaction[], transactionMainType: string) => {
  const filteredTransactions = portfolio.filter(
    (transaction) => transaction.TransactionMainType === transactionMainType
  );
  if (filteredTransactions.length === 0) {
    return undefined;
  }

  return filteredTransactions
    .map((transaction) => transaction.Tax)
    .reduce((prev, curr) => prev + curr, 0);
};

const sumForTotalCosts = (
  portfolio: Transaction[],
  transactionMainType: string
) => {
  const filteredTransactions = portfolio.filter(
    (transaction) => transaction.TransactionMainType === transactionMainType
  );
  if (filteredTransactions.length === 0) {
    return undefined;
  }

  return filteredTransactions
    .map((transaction) =>
      totalCosts(transaction.ServiceFee, transaction.Fee, transaction.Tax)
    )
    .reduce((prev, curr) => prev + curr, 0);
};

const sumForDirtyAmount = (
  portfolio: Transaction[],
  transactionMainType: string
) => {
  const filteredTransactions = portfolio.filter(
    (transaction) => transaction.TransactionMainType === transactionMainType
  );

  if (filteredTransactions.length === 0) {
    return undefined;
  }

  return filteredTransactions
    .map((transaction) => transaction.DirtyAmount)
    .reduce((prev, curr) => prev + curr, 0);
};

const purchasesSum = (data: Portfolio) => {
  const sums = {
    CleanAmount: sumForPrice(data.Transactions, "Osto"),
    Fee: sumForFee(data.Transactions, "Osto"),
    Tax: sumForTax(data.Transactions, "Osto"),
    TotalCosts: sumForTotalCosts(data.Transactions, "Osto"),
    DirtyAmount: sumForDirtyAmount(data.Transactions, "Osto"),
  };
  return Object.values(sums).some((x) => x !== undefined) ? sums : undefined;
};

const sellsSum = (data: Portfolio) => {
  const sums = {
    CleanAmount: sumForPrice(data.Transactions, "Myynti"),
    Fee: sumForFee(data.Transactions, "Myynti"),
    Tax: sumForTax(data.Transactions, "Myynti"),
    TotalCosts: sumForTotalCosts(data.Transactions, "Myynti"),
    DirtyAmount: sumForDirtyAmount(data.Transactions, "Myynti"),
  };
  return Object.values(sums).some((x) => x !== undefined) ? sums : undefined;
};

const depositsSum = (data: Portfolio) => {
  const sums = {
    CleanAmount: sumForPrice(data.Transactions, "Pano"),
    Fee: sumForFee(data.Transactions, "Pano"),
    Tax: sumForTax(data.Transactions, "Pano"),
    TotalCosts: sumForTotalCosts(data.Transactions, "Pano"),
    DirtyAmount: sumForDirtyAmount(data.Transactions, "Pano"),
  };
  return Object.values(sums).some((x) => x !== undefined) ? sums : undefined;
};

const withdrawsSum = (data: Portfolio) => {
  const sums = {
    CleanAmount: sumForPrice(data.Transactions, "Otto"),
    Fee: sumForFee(data.Transactions, "Otto"),
    Tax: sumForTax(data.Transactions, "Otto"),
    TotalCosts: sumForTotalCosts(data.Transactions, "Otto"),
    DirtyAmount: sumForDirtyAmount(data.Transactions, "Otto"),
  };
  return Object.values(sums).some((x) => x !== undefined) ? sums : undefined;
};

const cashSum = (data: Portfolio) => {
  const sums = {
    CleanAmount: sumForPrice(data.Transactions, "Käteinen"),
    Fee: sumForFee(data.Transactions, "Käteinen"),
    Tax: sumForTax(data.Transactions, "Käteinen"),
    TotalCosts: sumForTotalCosts(data.Transactions, "Käteinen"),
    DirtyAmount: sumForDirtyAmount(data.Transactions, "Käteinen"),
  };
  return Object.values(sums).some((x) => x !== undefined) ? sums : undefined;
};

const dividendsSum = (data: Portfolio) => {
  const sums = {
    CleanAmount: sumForPrice(data.Transactions, "Osinko"),
    Fee: sumForFee(data.Transactions, "Osinko"),
    Tax: sumForTax(data.Transactions, "Osinko"),
    TotalCosts: sumForTotalCosts(data.Transactions, "Osinko"),
    DirtyAmount: sumForDirtyAmount(data.Transactions, "Osinko"),
  };
  return Object.values(sums).some((x) => x !== undefined) ? sums : undefined;
};

const interestsSum = (data: Portfolio) => {
  const sums = {
    CleanAmount: sumForPrice(data.Transactions, "Korko"),
    Fee: sumForFee(data.Transactions, "Korko"),
    Tax: sumForTax(data.Transactions, "Korko"),
    TotalCosts: sumForTotalCosts(data.Transactions, "Korko"),
    DirtyAmount: sumForDirtyAmount(data.Transactions, "Korko"),
  };
  return Object.values(sums).some((x) => x !== undefined) ? sums : undefined;
};

const corporateActionsSum = (data: Portfolio) => {
  const sums = {
    CleanAmount: sumForPrice(data.Transactions, "Corporate Action"),
    Fee: sumForFee(data.Transactions, "Corporate Action"),
    Tax: sumForTax(data.Transactions, "Corporate Action"),
    TotalCosts: sumForTotalCosts(data.Transactions, "Corporate Action"),
    DirtyAmount: sumForDirtyAmount(data.Transactions, "Corporate Action"),
  };
  return Object.values(sums).some((x) => x !== undefined) ? sums : undefined;
};

export const sums = (data: Portfolio) => {
  return {
    CorporateActionsTotal: corporateActionsSum(data),
    InterestsTotal: interestsSum(data),
    CashTotal: cashSum(data),
    SellsTotal: sellsSum(data),
    DividendsTotal: dividendsSum(data),
    SubscriptionsTotal: purchasesSum(data),
    WithdrawsTotal: withdrawsSum(data),
    DepositsTotal: depositsSum(data),
  };
};
